import React, { useState } from 'react';
import styled from 'styled-components';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { IconButtonWrapper } from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import NoIcon from '@grnhse/seedling/lib/birch/icons/no';
import YesIcon from '@grnhse/seedling/lib/birch/icons/yes';
import type { IconProps } from '@grnhse/seedling/lib/birch/icons/base';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  FeedbackPopover,
  type FeedbackFormData,
} from 'shared/components/ai_feedback';

const t = withLocalePrefix(
  'talent_matching.configuration_assistant.response_feedback'
);
const tFeedback = withLocalePrefix('ai_feedback');

export const ASSISTANT_FEEDBACK_KEY = 'talent_matching_assistant_feedback';

export type AssistantResponseVote = 'up' | 'down';

// Stable analytics keys, positionally aligned with the
// `ai_feedback.talent_matching_assistant_feedback` lists; the locale labels
// are what the recruiter sees.
export const ASSISTANT_RESPONSE_REASON_KEYS: Record<
  AssistantResponseVote,
  string[]
> = {
  up: [
    'captured_intent',
    'suggested_new_skills',
    'good_starting_point',
    'saved_time',
  ],
  down: [
    'missed_description',
    'wrong_skills',
    'filters_wrong',
    'too_many_edits',
  ],
};

export type AssistantResponseReasons = {
  feedback: string[];
  comment?: string | null;
};

const SENTIMENT = { up: 'positive', down: 'negative' } as const;

type Props = {
  vote: AssistantResponseVote | null;
  onVote: (next: AssistantResponseVote | null) => void;
  onSubmitReasons: (
    vote: AssistantResponseVote,
    reasons: AssistantResponseReasons
  ) => void;
};

export function AssistantResponseFeedback({
  vote,
  onVote,
  onSubmitReasons,
}: Props) {
  const [isReasonsOpen, setIsReasonsOpen] = useState(false);
  const [votesWithReasons, setVotesWithReasons] = useState<
    AssistantResponseVote[]
  >([]);
  const hasSubmittedReasons = vote !== null && votesWithReasons.includes(vote);

  const toggle = (value: AssistantResponseVote) => {
    onVote(vote === value ? null : value);
  };

  const handleSubmit = (formData: FeedbackFormData) => {
    setIsReasonsOpen(false);
    const comment = formData.comment?.trim() || null;
    if (!vote || (formData.feedback.length === 0 && !comment)) {
      return;
    }
    const keys = ASSISTANT_RESPONSE_REASON_KEYS[vote];
    onSubmitReasons(vote, {
      feedback: formData.feedback.map((value) => keys[Number(value)] ?? value),
      comment,
    });
    setVotesWithReasons((current) => [...current, vote]);
  };

  return (
    <FeedbackPopover
      isOpen={isReasonsOpen && vote !== null}
      sentiment={SENTIMENT[vote ?? 'up']}
      feedbackKey={ASSISTANT_FEEDBACK_KEY}
      onClose={() => setIsReasonsOpen(false)}
      onSubmit={handleSubmit}
      showComments
      hasFeedbackOptions
    >
      <Controls>
        <VoteButton
          label={t('vote_up')}
          icon={YesIcon}
          selected={vote === 'up'}
          selectedColor={FullPalette.GREEN_700}
          onClick={() => toggle('up')}
        />
        <VoteButton
          label={t('vote_down')}
          icon={NoIcon}
          selected={vote === 'down'}
          selectedColor={FullPalette.POPPY_600}
          onClick={() => toggle('down')}
        />
        {hasSubmittedReasons && (
          <SecondaryBody metadata>{tFeedback('success')}</SecondaryBody>
        )}
        {vote && !hasSubmittedReasons && (
          <TertiaryButton size="sm" onClick={() => setIsReasonsOpen(true)}>
            {t('tell_us_more')}
          </TertiaryButton>
        )}
      </Controls>
    </FeedbackPopover>
  );
}

type VoteButtonProps = {
  label: string;
  icon: React.ComponentType<IconProps>;
  selected: boolean;
  selectedColor: string;
  onClick: () => void;
};

const VoteButton = ({
  label,
  icon: Icon,
  selected,
  selectedColor,
  onClick,
}: VoteButtonProps) => (
  <Tooltip label={label} ariaLabel={label} disablePortal={false}>
    <IconButtonWrapper
      type="button"
      size="sm"
      flat
      aria-label={label}
      aria-pressed={selected}
      onClick={onClick}
    >
      <Icon
        size="sm"
        color={selected ? selectedColor : FullPalette.EVERGREEN_700}
      />
    </IconButtonWrapper>
  </Tooltip>
);

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing['0.5']};
  padding-left: ${Tokens.spacing['1']};
`;
